import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import { Button } from "./ui/button";
import { LogOut, User, Mail, Globe, Download, Bell, BellOff, Twitter, Facebook, Instagram, Menu, X } from "lucide-react";
import BrandMark from "./BrandMark";
import LanguageToggle from "./LanguageToggle";
import { AppStoreBadge, GooglePlayBadge } from "./StoreBadges";
import { attachInstallPromptHandler, triggerInstallPrompt, requestNotificationPermission } from "../lib/pwa";

// Where each role's "my dashboard" link points, so the role logic is written
// once instead of as parallel ternary chains for href and label.
const DASHBOARDS = {
  admin: { href: "/admin", labelKey: "nav.admin" },
  subadmin: { href: "/admin", labelKey: "nav.admin" },
  trainer: { href: "/trainer", labelKey: "nav.trainerHub" },
  learner: { href: "/learner", labelKey: "nav.myBookings" },
};


export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [canInstall, setCanInstall] = useState(false);
  const [notifPerm, setNotifPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    attachInstallPromptHandler(setCanInstall);
  }, []);

  const onInstall = async () => {
    const outcome = await triggerInstallPrompt();
    if (outcome === "accepted") setCanInstall(false);
  };
  const onEnableNotif = async () => {
    const r = await requestNotificationPermission();
    setNotifPerm(r);
  };

  const dash = user ? (DASHBOARDS[user.role] || DASHBOARDS.learner) : null;
  const dashHref = dash ? dash.href : "/login";
  const dashLabel = dash ? t(dash.labelKey) : "";

  const go = (path) => { setMenuOpen(false); nav(path); };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="border-b border-border bg-background sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
          <Link to="/" data-testid="brand-link" onClick={() => setMenuOpen(false)}>
            <BrandMark size="md" />
          </Link>

          <nav className="hidden md:flex items-center gap-7 text-sm">
            <Link to="/trainers" className="hover:text-primary transition-colors" data-testid="nav-trainers">{t("nav.findTrainers")}</Link>
            {user && user.role === "learner" && <Link to="/learner" className="hover:text-primary transition-colors" data-testid="nav-learner">{t("nav.myBookings")}</Link>}
            {user && user.role === "trainer" && <Link to="/trainer" className="hover:text-primary transition-colors" data-testid="nav-trainer">{t("nav.trainerHub")}</Link>}
            {user && (user.role === "admin" || user.role === "subadmin") && <Link to="/admin" className="hover:text-primary transition-colors" data-testid="nav-admin">{t("nav.admin")}</Link>}
          </nav>

          <div className="flex items-center gap-1.5">
            {canInstall && (
              <Button variant="ghost" size="sm" onClick={onInstall} title="Install DriveTerrain" data-testid="install-pwa-btn">
                <Download className="w-4 h-4" />
              </Button>
            )}
            {user && notifPerm !== "granted" && notifPerm !== "denied" && (
              <Button variant="ghost" size="sm" onClick={onEnableNotif} title="Enable notifications" data-testid="enable-notif-btn">
                <BellOff className="w-4 h-4" />
              </Button>
            )}
            {user && notifPerm === "granted" && (
              <span className="hidden sm:inline" title="Notifications enabled" data-testid="notif-on">
                <Bell className="w-4 h-4 text-primary" />
              </span>
            )}
            <LanguageToggle />
            {user ? (
              <>
                <Link to={dashHref} className="hidden sm:flex items-center gap-2 text-sm hover:text-primary px-2 max-w-[10rem] truncate" data-testid="user-name">
                  <User className="w-4 h-4 shrink-0" /> <span className="truncate">{user.name}</span>
                </Link>
                <Button variant="ghost" size="sm" onClick={async () => { await logout(); nav("/"); }} data-testid="logout-btn">
                  <LogOut className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                {/* Auth CTAs move into the mobile sheet below to keep the bar
                    inside 360px viewports. */}
                <Button variant="ghost" size="sm" className="hidden md:inline-flex" onClick={() => nav("/login")} data-testid="header-login-btn">{t("nav.signin")}</Button>
                <Button size="sm" className="rounded-sm hidden md:inline-flex" onClick={() => nav("/register")} data-testid="header-register-btn">{t("nav.getStarted")}</Button>
              </>
            )}
            <Button variant="ghost" size="sm" className="md:hidden px-2"
                    aria-label={menuOpen ? "Close menu" : "Open menu"} aria-expanded={menuOpen}
                    onClick={() => setMenuOpen(o => !o)} data-testid="mobile-menu-toggle">
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {menuOpen && (
          <div className="md:hidden border-t border-border bg-background" data-testid="mobile-menu">
            <nav className="px-4 py-3 flex flex-col text-sm">
              <button onClick={() => go("/trainers")} className="py-3 text-left border-b border-border hover:text-primary transition-colors" data-testid="mobile-nav-trainers">{t("nav.findTrainers")}</button>
              {user && (
                <button onClick={() => go(dashHref)} className="py-3 text-left border-b border-border hover:text-primary transition-colors" data-testid="mobile-nav-dashboard">{dashLabel}</button>
              )}
              {user ? (
                <div className="py-3 flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2 text-muted-foreground min-w-0">
                    <User className="w-4 h-4 shrink-0" /> <span className="truncate">{user.name}</span>
                  </span>
                  <Button variant="outline" size="sm" className="rounded-sm shrink-0"
                          onClick={async () => { setMenuOpen(false); await logout(); nav("/"); }}
                          data-testid="mobile-logout-btn">
                    <LogOut className="w-4 h-4 mr-1.5" /> Sign out
                  </Button>
                </div>
              ) : (
                <div className="py-3 flex flex-col gap-2">
                  <Button variant="outline" className="rounded-sm w-full" onClick={() => go("/login")} data-testid="mobile-login-btn">{t("nav.signin")}</Button>
                  <Button className="rounded-sm w-full" onClick={() => go("/register")} data-testid="mobile-register-btn">{t("nav.getStarted")}</Button>
                </div>
              )}
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-border bg-background mt-12" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid lg:grid-cols-12 gap-10 text-sm">
          <div className="lg:col-span-4">
            <BrandMark size="md" />
            <p className="text-muted-foreground mt-3 leading-relaxed">{t("footer.tagline")}</p>
            <div className="text-xs text-muted-foreground mt-3">
              A product of <span className="text-foreground font-medium">Loriento Inc</span>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3" data-testid="footer-download-heading">Get the app</div>
            <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
              Find verified driving trainers, book road-test sessions, and practice CBT quizzes — anywhere in Canada.
            </p>
            <div className="flex flex-wrap items-center gap-3" data-testid="footer-download-badges">
              <AppStoreBadge href="#" testId="footer-appstore-badge" />
              <GooglePlayBadge href="#" testId="footer-googleplay-badge" />
            </div>
            <div className="text-[11px] text-muted-foreground mt-3 italic" data-testid="footer-store-coming-soon">
              Native apps launching soon · Available now as an installable web app.
            </div>
          </div>

          <div className="lg:col-span-3 space-y-3">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">{t("footer.contact")}</div>
            <a href="mailto:info@driveterrain.ca" className="flex items-center gap-2 hover:text-primary" data-testid="footer-email">
              <Mail className="w-4 h-4 text-primary" /> info@driveterrain.ca
            </a>
            <a href="https://driveterrain.ca" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-primary" data-testid="footer-website">
              <Globe className="w-4 h-4 text-primary" /> driveterrain.ca
            </a>
            <div className="flex items-center gap-3 pt-2" data-testid="footer-social">
              <a href="https://x.com/Drive_Terrain" target="_blank" rel="noopener noreferrer" aria-label="DriveTerrain on X (Twitter)"
                 className="w-9 h-9 rounded-sm border border-border flex items-center justify-center hover:border-primary hover:text-primary transition-colors"
                 data-testid="footer-social-twitter">
                <Twitter className="w-4 h-4" />
              </a>
              <a href="https://www.facebook.com/profile.php?id=61594465124501" target="_blank" rel="noopener noreferrer" aria-label="DriveTerrain on Facebook"
                 className="w-9 h-9 rounded-sm border border-border flex items-center justify-center hover:border-primary hover:text-primary transition-colors"
                 data-testid="footer-social-facebook">
                <Facebook className="w-4 h-4" />
              </a>
              <a href="https://www.instagram.com/driveterrain_canada" target="_blank" rel="noopener noreferrer" aria-label="DriveTerrain on Instagram"
                 className="w-9 h-9 rounded-sm border border-border flex items-center justify-center hover:border-primary hover:text-primary transition-colors"
                 data-testid="footer-social-instagram">
                <Instagram className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
        <div className="border-t border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <span>© {new Date().getFullYear()} Loriento Inc. All rights reserved.</span>
              <Link to="/privacy" className="hover:text-primary underline" data-testid="footer-privacy">Privacy</Link>
              <Link to="/terms" className="hover:text-primary underline" data-testid="footer-terms">Terms</Link>
            </div>
            <div className="flex items-center gap-1">
              <span className="inline-block w-4 h-3 mr-1" aria-label="CA flag">
                <svg viewBox="0 0 6 3" className="w-full h-full">
                  <rect width="6" height="3" fill="#fff"/>
                  <rect width="1.5" height="3" fill="#FF0000"/>
                  <rect x="4.5" width="1.5" height="3" fill="#FF0000"/>
                  <path d="M3 0.6 L3.15 1 L3.6 1 L3.25 1.3 L3.4 1.7 L3 1.5 L2.6 1.7 L2.75 1.3 L2.4 1 L2.85 1 Z" fill="#FF0000"/>
                </svg>
              </span>
              {t("footer.proudly")}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
